import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../Api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faFileText,
  faSpinner,
  faInfoCircle,
  faChevronRight,
  faMapLocationDot
} from '@fortawesome/free-solid-svg-icons';

const SupervisorQuotas = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [quotaData, setQuotaData] = useState([]);

  useEffect(() => {
    fetchMyQuotas();
  }, []);

  const fetchMyQuotas = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get('/quotas/my-quotas');
      if (response.data.success) {
        setQuotaData(response.data.data || []);
      } else {
        setError('Erreur lors du chargement des quotas');
        setQuotaData([]);
      }
    } catch (error) {
      console.error('Error fetching quotas:', error);
      setError('Erreur lors du chargement des quotas');
      setQuotaData([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FontAwesomeIcon icon={faSpinner} className="animate-spin text-4xl text-red-600 mb-4" />
          <p className="text-gray-600">Chargement des quotas...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FontAwesomeIcon icon={faInfoCircle} className="text-4xl text-red-600 mb-4" />
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (quotaData.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }}></div>
        </div>
        
        <div className="relative z-10 p-6">
          <header className="mb-6">
            <h1 className="text-3xl font-bold text-gray-800">Mes Quotas</h1>
          </header>
          
          <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-8 text-center">
            <FontAwesomeIcon icon={faFileText} className="text-4xl text-gray-300 mb-4" />
            <p className="text-lg text-gray-500">Aucun quota assigné</p>
            <p className="text-sm text-gray-400 mt-2">Les quotas qui vous sont assignés apparaîtront ici</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}></div>
      </div>
      
      <div className="relative z-10 p-6">
        <header className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Mes Quotas</h1>
            <p className="text-gray-600 mt-2">Liste des projets avec quotas assignés</p>
          </div>
          <button
            onClick={() => navigate('/supervisor/addresses-map')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors self-start sm:self-auto"
          >
            <FontAwesomeIcon icon={faMapLocationDot} />
            Carte des adresses
          </button>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {quotaData.map((project) => (
            <div 
              key={project.project_id} 
              onClick={() => navigate(`/supervisor/my-quotas/${project.project_id}`)}
              className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 overflow-hidden cursor-pointer hover:shadow-xl transition-shadow duration-200"
            >
              {/* Survey Header */}
              <div className="bg-gradient-to-r from-red-600 to-red-700 p-6 text-white">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h2 className="text-xl font-bold mb-2">{project.survey.title}</h2>
                    {project.survey_wave?.label && (
                      <span className="inline-block mb-2 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-white/25 text-white">
                        {project.survey_wave.label}
                      </span>
                    )}
                    <div className="text-sm opacity-90">
                      <span>ID: {project.survey.limesurvey_id}</span>
                    </div>
                  </div>
                  <FontAwesomeIcon icon={faChevronRight} className="text-xl opacity-80" />
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-white/20">
                  <span className="text-xs opacity-90">Statut: </span>
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold bg-white ${
                    project.project_status === 'locked' 
                      ? 'text-green-600' 
                      : 'text-gray-600'
                  }`}>
                    {project.project_status === 'locked' ? 'Ouvert' : 'Brouillon'}
                  </span>
                </div>
              </div>

              {/* Summary Stats */}
              <div className="p-6">
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <div className="text-xs text-gray-600 mb-1">Quotas</div>
                    <div className="text-2xl font-bold text-blue-600">{project.total_quotas}</div>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <div className="text-xs text-gray-600 mb-1">Mes complétés</div>
                    <div className="text-2xl font-bold text-green-600">{(project.my_total_completes || 0).toLocaleString()}</div>
                  </div>
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Mon quota assigné:</span>
                    <span className="font-semibold text-gray-900">{(project.my_total_assigned || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Mes complétés:</span>
                    <span className="font-semibold text-green-600">{(project.my_total_completes || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Restants:</span>
                    <span className="font-semibold text-orange-600">{(project.my_total_remaining || 0).toLocaleString()}</span>
                  </div>
                  {project.survey?.price_per_complete != null && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Prix par complétion:</span>
                      <span className="font-semibold text-gray-900">
                        {Number(project.survey.price_per_complete).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MAD
                      </span>
                    </div>
                  )}
                  {project.fieldwork_start_date && (
                    <div className="flex justify-between pt-2 border-t border-gray-100">
                      <span className="text-gray-600">Période:</span>
                      <span className="text-xs text-gray-700">
                        {formatDate(project.fieldwork_start_date)} - {formatDate(project.fieldwork_end_date)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SupervisorQuotas;
